<template>
  <div class="rba-container">
    <ToolBar
      class="rba-toolbar"
      :selected-label="selectedLabel"
      :task-id="props.taskId"
      :ruler-available="measurementAvailable"
    />
    <div class="rba-content">
      <aside class="rba-panel rba-panel--slides">
        <header class="rba-panel__header">
          <p class="rba-panel__title">
            Slides
          </p>
          <span class="rba-panel__meta">{{ Object.keys(images).length }} items</span>
        </header>
        <SlideList
          :columns="props.slideColumns"
          :images="images"
          :current-selected-slide="currentSelectedSlide"
          :interactive="props.interactive"
          @select="handleSelectSlide"
          @delete="handleDeleteSlide"
          @restore="handleRestoreSlide"
        />
      </aside>

      <section class="rba-stage">
        <SlideCanvas
          ref="canvasRef"
          :key="currentSelectedSlide || 'slide-canvas'"
          :width="props.canvasWidth"
          :height="props.canvasHeight"
          :interactive="props.interactive"
          :background="stageBackground"
          :background-image="backgroundImage"
          :regions="resolvedRegions"
          :auto-sync="true"
          :label-filter="labelFilter"
          :active-label="selectedLabel"
          :task-id="props.taskId"
          :image-id="currentSelectedSlide || ''"
          :effective-mpp-x="measurementMetadata?.x"
          :effective-mpp-y="measurementMetadata?.y"
          fallback-label="default"
          @update:regions="handleRegionsUpdate"
          @box-contextmenu="handleBoxContextMenu"
        />
        <div
          v-if="!hasActiveSlide"
          class="rba-stage__placeholder"
        >
          <p>Select a slide to start annotating.</p>
        </div>
      </section>

      <aside class="rba-panel rba-panel--filters">
        <header class="rba-panel__header">
          <p class="rba-panel__title">
            Labels
          </p>
        </header>
        <LabelFilter
          :images="labelFilterImages"
          :label-filter="labelFilter"
          @toggle="handleLabelToggle"
        />
      </aside>
    </div>
    <CellTypePopup
      :visible="popupState.visible"
      :position="popupPosition"
      :current-label="popupState.currentLabel"
      @select="handlePopupSelect"
      @close="closePopup"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import SlideList from '@/components/RegionBasedAnnotation/components/SlideList.vue';
import SlideCanvas from '@/components/RegionBasedAnnotation/components/SlideCanvas.vue';
import LabelFilter from '@/components/RegionBasedAnnotation/components/LabelFilter.vue';
import ToolBar from '@/views/RBC/components/ToolBar.vue';
import CellTypePopup from '@/views/RBC/components/CellTypePopup.vue';
import type { SlideRegionDict, SlideRenderBox } from '@/components/RegionBasedAnnotation/components/SlideCanvas.types';
import type { CellTypeMenuOption } from '@/utils/constants/cellTypeMenuConfig';
import { resolveEffectiveMpp } from '@/utils/measurementRuler';
import { useMeasurementRulerStore } from '@/store';
import { useRegionBasedAnnotation } from './useRegionBasedAnnotation';

const props = withDefaults(defineProps<{
    canvasWidth?: number;
    canvasHeight?: number;
    slideColumns?: number;
    interactive?: boolean;
    taskId?: string;
    effectiveMppX?: number | null;
    effectiveMppY?: number | null;
}>(), {
    canvasWidth: 700,
    canvasHeight: 700,
    slideColumns: 1,
    interactive: true,
    taskId: '',
    effectiveMppX: null,
    effectiveMppY: null,
});

const {
    images,
    labelFilter,
    selectedLabel,
    currentSelectedSlide,
    labelFilterImages,
    canvasRegions,
    backgroundImage,
    handleSelectSlide,
    handleLabelToggle,
    handleRegionsUpdate,
    handleDeleteSlide,
    handleRestoreSlide,
} = useRegionBasedAnnotation();

const hasActiveSlide = computed(() => Boolean(currentSelectedSlide.value));
const measurementMetadata = computed(() =>
  resolveEffectiveMpp(props.effectiveMppX, props.effectiveMppY),
);
const rulerStore = useMeasurementRulerStore();
const measurementAvailable = computed(() => (
  Boolean(props.taskId && measurementMetadata.value)
  && rulerStore.hasRenderableSurface(props.taskId)
));
const resolvedRegions = computed<SlideRegionDict | undefined>(() => canvasRegions.value ?? undefined);
const stageBackground = computed(() => (hasActiveSlide.value ? '#ffffff' : '#f3f4f6'));
const canvasRef = ref<InstanceType<typeof SlideCanvas> | null>(null);

const popupPosition = reactive({ x: 0, y: 0 });
const popupState = reactive({
  visible: false,
  currentLabel: '',
  targetBox: null as SlideRenderBox | null,
});

type BoxContextMenuPayload = {
  box: SlideRenderBox;
  clientX: number;
  clientY: number;
};

function handleBoxContextMenu(payload: BoxContextMenuPayload) {
  popupPosition.x = payload.clientX;
  popupPosition.y = payload.clientY;
  popupState.visible = true;
  popupState.targetBox = payload.box;
  popupState.currentLabel = payload.box.label;
}

function closePopup() {
  popupState.visible = false;
  popupState.targetBox = null;
  popupState.currentLabel = '';
}

function handlePopupSelect(option: CellTypeMenuOption) {
  if (!popupState.targetBox) return;
  const nextLabel = option.label || option.name;
  if (!nextLabel) return;
  const updated = canvasRef.value?.updateBoxLabel(popupState.targetBox.id, nextLabel) ?? false;
  if (!updated) {
    return;
  }
  closePopup();
}

watch(currentSelectedSlide, () => {
  closePopup();
});

watch(hasActiveSlide, (active) => {
  if (!active) {
    closePopup();
  }
});

watch(
  () => props.interactive,
  (interactive) => {
    if (!interactive) {
      closePopup();
    }
  },
);
</script>

<style scoped>
/* Compact layout with constrained panels and scrollable content */
.rba-container {
  padding: 0px;
  margin-top: 70px;
  min-height: calc(100vh - 70px);
  font-family: 'Space Grotesk', 'Segoe UI', sans-serif;
}

.rba-toolbar {
  margin-bottom: 16px;
}

.rba-content {
  padding-right: 10px;
  display: grid;
  grid-template-columns: 280px minmax(480px, 1fr) 320px;
  gap: 20px;
  max-height: calc(100vh - 160px);
  height: calc(100vh - 160px);
}

.rba-panel {
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 12px;
    padding: 16px;
    background: white;
    overflow: hidden;
}

/* Left slides: header + scrollable list area */
.rba-panel--slides {
    display: flex;
    flex-direction: column;
}

.rba-panel--slides>.slide-list-wrapper {
    flex: 1 1 0;
    min-height: 600px;
    overflow-y: auto;
}

.rba-panel__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 12px;
}

/* Center stage and right filters constrained to viewport */
.rba-stage,
.rba-panel--filters {
    max-height: calc(100vh - 160px);
    min-height: 0;
    overflow: auto;
}

.rba-stage {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border: 1px solid rgba(2, 6, 35, 0.06);
}

@media (max-width: 1200px) {
    .rba-content {
        grid-template-columns: 1fr;
    }

    .rba-panel--slides,
    .rba-panel--filters {
        order: 2
    }

    .rba-stage {
        order: 1
    }
}
</style>
